import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDataProvider, useGetList, useNotify, useRefresh } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2,
  Download,
  FileText,
  Filter,
  PlayCircle,
  Search,
} from "lucide-react";
import { PageHeader } from "../common/PageHeader";
import type { CrmDataProvider } from "../../providers/types";

type QueueRow = {
  id: number;
  dc_id: number;
  dc_reference: string;
  challan_date: string;
  company_id: number | null;
  customer_name: string | null;
  customer_state: string | null;
  project_id: number | null;
  pi_id: number | null;
  dc_status: string;
  total_quantity: number;
  item_count: number;
};

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

export const AutoInvoicingPage = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [selected, setSelected] = useState<number[]>([]);
  const [busyIds, setBusyIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useGetList<QueueRow>("dispatch_to_invoice_queue", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "challan_date", order: "DESC" },
  });

  const rows = useMemo(
    () =>
      (data ?? []).filter((row) =>
        search
          ? (row.customer_name ?? "").toLowerCase().includes(search.toLowerCase()) ||
            row.dc_reference.toLowerCase().includes(search.toLowerCase())
          : true,
      ),
    [data, search],
  );

  const toggleAll = () =>
    setSelected(
      selected.length === rows.length ? [] : rows.map((r) => r.dc_id),
    );
  const toggleOne = (id: number) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const generate = async (dcIds: number[]) => {
    setBusyIds((p) => [...p, ...dcIds]);
    try {
      const result = await dataProvider.autoInvoiceFromDC(dcIds);
      const invoiced = result.invoiceIds.length;
      const failed = result.errors.length;
      if (invoiced > 0) {
        notify(`Generated ${invoiced} invoice(s).`, { type: "success" });
      }
      if (failed > 0) {
        notify(
          `${failed} dispatch(es) could not be invoiced. See console for details.`,
          { type: "warning" },
        );
        console.warn("[auto-invoice] errors", result.errors);
      }
      refresh();
      setSelected([]);
    } catch (error) {
      console.error(error);
      notify(`Failed to generate invoices: ${(error as Error).message}`, {
        type: "error",
      });
    } finally {
      setBusyIds((p) => p.filter((x) => !dcIds.includes(x)));
    }
  };

  return (
    <div className="p-6 bg-slate-50/30 min-h-screen">
      <PageHeader
        title="Auto Invoice from Dispatch"
        description="Sales invoices generated automatically from dispatched delivery challans."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-white border-l-4 border-l-primary shadow-sm">
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">
                Queue Total
              </p>
              <h3 className="text-2xl font-bold text-slate-900">
                {(data ?? []).length} dispatches
              </h3>
            </div>
            <FileText className="h-8 w-8 text-slate-300" />
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">
                Selected
              </p>
              <h3 className="text-2xl font-bold text-emerald-600">
                {selected.length}
              </h3>
            </div>
            <CheckCircle2 className="h-8 w-8 text-emerald-200" />
          </CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-amber-500 shadow-sm">
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">
                Filtered
              </p>
              <h3 className="text-2xl font-bold text-slate-900">
                {rows.length}
              </h3>
            </div>
            <PlayCircle className="h-8 w-8 text-amber-200" />
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-none border border-slate-200 bg-white overflow-hidden">
        <CardHeader className="border-b bg-slate-50/50 flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base font-bold text-slate-900">
            Queue for Invoicing
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="relative max-w-xs hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search DC / customer..."
                className="pl-8 h-8 bg-white border-slate-300 text-xs shadow-none w-[220px]"
              />
            </div>
            {selected.length > 0 && (
              <Badge className="bg-primary text-white px-3 py-1 shadow-sm animate-in slide-in-from-right-4 duration-200">
                {selected.length} Selected
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8 font-medium border-slate-300 text-xs"
              disabled
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" /> Filter
            </Button>
            {selected.length > 0 ? (
              <Button
                onClick={() => generate(selected)}
                disabled={busyIds.length > 0}
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-700 font-bold shadow-md text-xs gap-2"
              >
                {busyIds.length > 0 ? (
                  "Processing..."
                ) : (
                  <>
                    <PlayCircle className="w-3.5 h-3.5" /> Generate Selected
                  </>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-8 bg-slate-900 text-white font-bold text-xs"
                disabled
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export List
              </Button>
            )}
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200 w-10">
                  <Checkbox
                    checked={rows.length > 0 && selected.length === rows.length}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Dispatch ID
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Customer
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Date
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">
                  Items
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    Loading queue…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-slate-400"
                  >
                    No dispatched DCs awaiting invoicing.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const isBusy = busyIds.includes(row.dc_id);
                return (
                  <tr
                    key={row.dc_id}
                    className={`${selected.includes(row.dc_id) ? "bg-blue-50/40" : "even:bg-slate-50/50 hover:bg-slate-100/50"} border-b border-slate-200 transition-all`}
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 align-middle">
                      <Checkbox
                        checked={selected.includes(row.dc_id)}
                        onCheckedChange={() => toggleOne(row.dc_id)}
                        disabled={isBusy}
                      />
                    </td>
                    <td
                      className="px-4 py-3.5 text-sm border-r border-slate-200 font-mono font-bold text-primary hover:underline cursor-pointer align-middle"
                      onClick={() => navigate(`/delivery_challans/${row.dc_id}/show`)}
                    >
                      {row.dc_reference}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-semibold align-middle">
                      {row.customer_name ?? "—"}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-slate-500 align-middle">
                      {fmtDate(row.challan_date)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-black text-slate-900 align-middle">
                      {row.item_count}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      <Button
                        disabled={isBusy}
                        onClick={() => generate([row.dc_id])}
                        size="sm"
                        className="h-8 font-bold text-xs shadow-sm transition-all bg-slate-100 text-slate-700 hover:bg-emerald-600 hover:text-white"
                      >
                        {isBusy ? "Creating…" : "Generate Invoice"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
